<template>
  <v-app>
    <!-- on mobile a fixed bottom navigation is used to change tabs -->
    <v-content :class="$vuetify.breakpoint.xs ? 'mobile' : ''">
      <v-bottom-navigation
        v-model="tab"
        v-if="$vuetify.breakpoint.xs"
        color="primary"
        mandatory
        app
        >
        <v-btn
          text
          value="locations"
          @click="tabInput('locations')"
          class="px-1"
          >
          <span :class="bottomNavTextClass">{{translations['Locations']}}</span>
          <v-img
            max-height="24"
            max-width="24"
            min-width="24"
            contain
            :src="require('./assets/location.svg')"
            ></v-img>
        </v-btn>
        <v-btn
          text
          value="impacted"
          @click="tabInput('impacted')"
          class="px-1"
          >
          <span :class="bottomNavTextClass">{{translations['Impacted']}}</span>
          <v-img
            max-height="24"
            max-width="24"
            min-width="24"
            contain
            :src="require('./assets/impacted.svg')"
            ></v-img>
        </v-btn>
        <v-btn
          text
          value="partially-impacted"
          @click="tabInput('partially-impacted')"
          class="px-1"
          >
          <span :class="bottomNavTextClass">{{translations['Partially Impacted']}}</span>
          <v-img
            max-height="24"
            max-width="24"
            min-width="24"
            contain
            :src="require('./assets/partially-impacted.svg')"
            ></v-img>
        </v-btn>
        <v-btn
          text
          value="smoke-impacted"
          @click="tabInput('smoke-impacted')"
          class="px-1"
          >
          <span :class="bottomNavTextClass">{{translations['Smoke Impacted']}}</span>
          <v-img
            max-height="24"
            max-width="24"
            min-width="24"
            contain
            :src="require('./assets/smoke-impacted.svg')"
            ></v-img>
        </v-btn>
        <v-btn
          text
          value="not-impacted"
          @click="tabInput('not-impacted')"
          class="px-1"
          >
          <span :class="bottomNavTextClass">{{translations['Not Impacted']}}</span>
          <v-img
            max-height="24"
            max-width="24"
            min-width="24"
            contain
            :src="require('./assets/not-impacted.svg')"
            ></v-img>
        </v-btn>
      </v-bottom-navigation>

      <!-- on desktop a fixed left navigation drawer is used to contain tabs and Info -->
      <v-navigation-drawer
        app
        v-if="!$vuetify.breakpoint.xs"
        touchless
        permanent
        :width="panelWidth + 15"
        >
        <v-tabs
          v-model="tab"
          class="mt-0 mb-0"
          icons-and-text
          centered
          fixed-tabs
          show-arrows
          :height="navDrawerHeight"
          hide-slider
          >
          <v-tabs-slider></v-tabs-slider>
          <v-tab
            href="#locations"
            @click="$gtag.event('Click', { 'event_category': 'Bushfire Map Filter', 'event_label': 'All Locations' })"
            >
            <span :class="tabLabelClass">{{translations['Locations']}}</span>
            <v-avatar
              color="lightgrey"
              size="42"
              min-height="42"
              tile
              >
              <v-img
                max-height="32"
                max-width="32"
                contain
                :src="require('./assets/location.svg')"
                ></v-img>
            </v-avatar>
          </v-tab>
          <v-tab-item value="locations">
            <v-list v-if="loading">
              <v-skeleton-loader type="list-item"></v-skeleton-loader>
              <v-skeleton-loader type="list-item"></v-skeleton-loader>
            </v-list>
            <div v-if="!loading && allLocations.length === 0" class="pa-6 text-center font-italic">No locations found</div>
            <v-list class="mx-4" v-show="!selectedFeature">
              <v-list-item-group v-model="location">
                <v-list-item
                  v-for="(item, i) in allLocations"
                  :key="i"
                  @click="selectFeature(item); $gtag.event('Click', { 'event_category': 'Bushfire Map Filtered Results', 'event_label': `${item.properties.Destination}|${item.properties.Status}` })"
                  @mousenter="listItemHover(item)"
                  @mouseleave="listItemDeHover(item)"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{item.properties.Destination}}{{item.properties.Including}}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-avatar tile>
                    <v-img :src="locationIcon(item.properties.Status, item.properties['Status Icon'])" max-height="32" max-width="32" contain></v-img>
                  </v-list-item-avatar>
                </v-list-item>
              </v-list-item-group>
            </v-list>
            <v-slide-x-transition>
              <Info
                v-show="selectedFeature"
                v-bind="selectedFeature ? iconKey(normaliseKeys(selectedFeature.properties)) : {}"
                @clearFeature="clearFeature"
                class="ma-4"
                ></Info>
            </v-slide-x-transition>
          </v-tab-item>
          <v-tab
            href="#impacted"
            @click="$gtag.event('Click', { 'event_category': 'Bushfire Map Filter', 'event_label': 'Impacted' })"
            >
            <span :class="tabLabelClass">{{translations['Impacted']}}</span>
            <v-avatar
              color="lightgrey"
              size="42"
              min-height="42"
              tile
              >
              <v-img
                max-height="32"
                max-width="32"
                contain
                :src="require('./assets/impacted.svg')"
                ></v-img>
            </v-avatar>
          </v-tab>
          <v-tab-item value="impacted">
            <div v-if="impactedLocations.length === 0" class="pa-6 text-center font-italic">No impacted locations found</div>
            <v-list class="mx-4" v-show="!selectedFeature">
              <v-list-item-group v-model="location">
                <v-list-item
                  v-for="(item, i) in impactedLocations"
                  :key="i"
                  @click="selectFeature(item); $gtag.event('Click', { 'event_category': 'Bushfire Map Filtered Results', 'event_label': `${item.properties.Destination}|${item.properties.Status}` })"
                  @mousenter="listItemHover(item)"
                  @mouseleave="listItemDeHover(item)"
                  >
                  <v-list-item-content>
                    <v-list-item-title>{{item.properties.Destination}}{{item.properties.Including}}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-avatar tile>
                    <v-img :src="locationIcon(item.properties.Status, item.properties['Status Icon'])" max-height="32" max-width="32" contain></v-img>
                  </v-list-item-avatar>
                </v-list-item>
              </v-list-item-group>
            </v-list>
            <v-slide-x-transition>
              <Info
                v-show="selectedFeature"
                v-bind="selectedFeature ? iconKey(normaliseKeys(selectedFeature.properties)) : {}"
                @clearFeature="clearFeature"
                class="ma-4"
                ></Info>
            </v-slide-x-transition>
          </v-tab-item>
          <v-tab
            href="#partially-impacted"
            @click="$gtag.event('Click', { 'event_category': 'Bushfire Map Filter', 'event_label': 'Partially Impacted' })"
            >
            <span :class="tabLabelClass">{{translations['Partially Impacted']}}</span>
            <v-avatar
              color="lightgrey"
              size="42"
              min-height="42"
              tile
              >
              <v-img
                max-height="32"
                max-width="32"
                contain
                :src="require('./assets/partially-impacted.svg')"
                ></v-img>
            </v-avatar>
          </v-tab>
          <v-tab-item value="partially-impacted">
            <div v-if="partiallyImpactedLocations.length === 0" class="pa-6 text-center font-italic">No partially impacted locations found</div>
            <v-list class="mx-4" v-show="!selectedFeature">
              <v-list-item-group v-model="location">
                <v-list-item
                  v-for="(item, i) in partiallyImpactedLocations"
                  :key="i"
                  @click="selectFeature(item); $gtag.event('Click', { 'event_category': 'Bushfire Map Filtered Results', 'event_label': `${item.properties.Destination}|${item.properties.Status}` })"
                  @mousenter="listItemHover(item)"
                  @mouseleave="listItemDeHover(item)"
                  >
                  <v-list-item-content>
                    <v-list-item-title>{{item.properties.Destination}}{{item.properties.Including}}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-avatar tile>
                    <v-img :src="locationIcon(item.properties.Status, item.properties['Status Icon'])" max-height="32" max-width="32" contain></v-img>
                  </v-list-item-avatar>
                </v-list-item>
              </v-list-item-group>
            </v-list>
            <v-slide-x-transition>
              <Info
                v-show="selectedFeature"
                v-bind="selectedFeature ? iconKey(normaliseKeys(selectedFeature.properties)) : {}"
                @clearFeature="clearFeature"
                class="ma-4"
                ></Info>
            </v-slide-x-transition>
          </v-tab-item>
          <v-tab
            href="#smoke-impacted"
            @click="$gtag.event('Click', { 'event_category': 'Bushfire Map Filter', 'event_label': 'Smoke Impacted' })"
            >
            <span :class="tabLabelClass">{{translations['Smoke Impacted']}}</span>
            <v-avatar
              color="lightgrey"
              size="42"
              min-height="42"
              tile
              >
              <v-img
                max-height="32"
                max-width="32"
                contain
                :src="require('./assets/smoke-impacted.svg')"
                ></v-img>
            </v-avatar>
          </v-tab>
          <v-tab-item value="smoke-impacted">
            <div v-if="smokeImpactedLocations.length === 0" class="pa-6 text-center font-italic">No smoke impacted locations found</div>
            <v-list class="mx-4" v-show="!selectedFeature">
              <v-list-item-group v-model="location">
                <v-list-item
                  v-for="(item, i) in smokeImpactedLocations"
                  :key="i"
                  @click="selectFeature(item); $gtag.event('Click', { 'event_category': 'Bushfire Map Filtered Results', 'event_label': `${item.properties.Destination}|${item.properties.Status}` })"
                  @mousenter="listItemHover(item)"
                  @mouseleave="listItemDeHover(item)"
                  >
                  <v-list-item-content>
                    <v-list-item-title>{{item.properties.Destination}}{{item.properties.Including}}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-avatar tile>
                    <v-img :src="locationIcon(item.properties.Status, item.properties['Status Icon'])" max-height="32" max-width="32" contain></v-img>
                  </v-list-item-avatar>
                </v-list-item>
              </v-list-item-group>
            </v-list>
            <v-slide-x-transition>
              <Info
                v-show="selectedFeature"
                v-bind="selectedFeature ? iconKey(normaliseKeys(selectedFeature.properties)) : {}"
                @clearFeature="clearFeature"
                class="ma-4"
                ></Info>
            </v-slide-x-transition>
          </v-tab-item>
          <v-tab
            href="#not-impacted"
            @click="$gtag.event('Click', { 'event_category': 'Bushfire Map Filter', 'event_label': 'Not Impacted' })"
            >
            <span :class="tabLabelClass">{{translations['Not Impacted']}}</span>
            <v-avatar
              color="lightgrey"
              size="42"
              min-height="42"
              tile
              >
              <v-img
                max-height="32"
                max-width="32"
                contain
                :src="require('./assets/not-impacted.svg')"
                ></v-img>
            </v-avatar>
          </v-tab>
          <v-tab-item value="not-impacted">
            <div v-if="notImpactedLocations.length === 0" class="pa-6 text-center font-italic">No unimpacted locations found</div>
            <v-list class="mx-4" v-show="!selectedFeature">
              <v-list-item-group v-model="location">
                <v-list-item
                  v-for="(item, i) in notImpactedLocations"
                  :key="i"
                  @click="selectFeature(item); $gtag.event('Click', { 'event_category': 'Bushfire Map Filtered Results', 'event_label': `${item.properties.Destination}|${item.properties.Status}` })"
                  @mousenter="listItemHover(item)"
                  @mouseleave="listItemDeHover(item)"
                  >
                  <v-list-item-content>
                    <v-list-item-title>{{item.properties.Destination}}{{item.properties.Including}}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-avatar tile>
                    <v-img :src="locationIcon(item.properties.Status, item.properties['Status Icon'])" max-height="32" max-width="32" contain></v-img>
                  </v-list-item-avatar>
                </v-list-item>
              </v-list-item-group>
            </v-list>
            <v-slide-x-transition>
              <Info
                v-show="selectedFeature"
                v-bind="selectedFeature ? iconKey(normaliseKeys(selectedFeature.properties)) : {}"
                @clearFeature="clearFeature"
                class="ma-4"
                ></Info>
            </v-slide-x-transition>
          </v-tab-item>
        </v-tabs>
        <template v-slot:append>
          <v-expansion-panels hover>
            <v-expansion-panel>
              <v-expansion-panel-header color="primary" expand-icon="mdi-chevron-up"><span class="white--text">{{translations['Disclaimer']}}</span></v-expansion-panel-header>
              <v-expansion-panel-content class="py-2">
                <em>{{translations['Disclaimer_Text1']}}<a target="_blank" :href="translations['Disclaimer_Link1_URL']" v-on:click.stop="getOutboundLink('Bushfire Map Disclaimer', translations['Disclaimer_Link1_URL'], true)">{{translations['Disclaimer_Link1_Text']}}</a>{{translations['Disclaimer_Text2']}}<a target="_blank" :href="translations['Disclaimer_Link2_URL']" v-on:click.stop="getOutboundLink('Bushfire Map Disclaimer', translations['Disclaimer_Link2_URL'], true)">{{translations['Disclaimer_Link2_Text']}}</a>{{translations['Disclaimer_Text3']}}</em>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>
      </v-navigation-drawer>
      <!-- on mobile a modal is used to show info -->
      <v-dialog v-if="$vuetify.breakpoint.xs" v-model="infoModal">
        <v-card class="pa-2">
          <Info
            v-show="selectedFeature"
            v-bind="selectedFeature ? iconKey(normaliseKeys(selectedFeature.properties)) : {}"
            @clearFeature="clearFeature"
            class="ma-4"
            ></Info>
        </v-card>
      </v-dialog>
      <v-dialog v-if="$vuetify.breakpoint.xs" v-model="disclaimerModal">
        <v-card class="pa-2">
          <p class="headline text--primary">
            {{translations['Disclaimer']}}
          </p>
            <em>{{translations['Disclaimer_Text1']}}<a target="_blank" :href="translations['Disclaimer_Link1_URL']" v-on:click.stop="getOutboundLink('Bushfire Map Disclaimer', translations['Disclaimer_Link1_URL'], true)">{{translations['Disclaimer_Link1_Text']}}</a>{{translations['Disclaimer_Text2']}}<a target="_blank" :href="translations['Disclaimer_Link2_URL']" v-on:click.stop="getOutboundLink('Bushfire Map Disclaimer', translations['Disclaimer_Link2_URL'], true)">{{translations['Disclaimer_Link2_Text']}}</a>{{translations['Disclaimer_Text3']}}</em>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              @click="disclaimerModal = false"
              >
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-snackbar
        v-if="$vuetify.breakpoint.xs"
        v-model="mobileDisclaimerButton"
        left
        bottom
        color="primary lighten-2"
        :timeout="0"
        >
        <v-btn @click="disclaimerModal = true" small text class="ma-0">{{translations['Disclaimer']}}</v-btn>
      </v-snackbar>
      <div id="map">
        <span v-if="!mapboxglSupported">Unfortunately your browser is not supported.</span>
      </div>
    </v-content>
  </v-app>
</template>

<script>
import Info from './Info'

import queryString from 'query-string'
import axios from 'axios'

import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

const initialMapBounds = [[112.471, -43.777], [155.801, -9.630]]
const initialMapBoundsOptions = { padding: 40 }

export default {
  name: 'App',
  components: {
    Info
  },
  created () {
    this.lang = queryString.parse(window.location.search).lang || 'en'
    this.cn = queryString.parse(window.location.search).cn
    this.cng = queryString.parse(window.location.search).cng
    this.mapBounds = queryString.parse(window.location.search).bbox
  },
  mounted () {
    mapboxgl.accessToken = this.cn ? 'pk.eyJ1IjoidG91cmlzbWF1c3RyYWxpYSIsImEiOiJjamFpdDMxeGYyMnA2MndxdXh3anFjemN1In0.ic3fKBCwZQzvbYZikrOdwA' : 'pk.eyJ1IjoidG91cmlzbWF1c3RyYWxpYSIsImEiOiJjazVhZTBxdHYwejl4M2RvMzl1OXpzNDFnIn0.KkUfd4b54ddW_uj9jzORDw'

    if (this.cn) {
      mapboxgl.config.API_URL = 'https://api.mapbox.cn'
    }
    if (this.cng) {
      mapboxgl.config.API_URL = 'https://api-global.mapbox.cn'
    }

    this.mapboxglSupported = mapboxgl.supported()

    const getLocationsPromise = new Promise((resolve) => {
      axios.get(`locations_${this.lang}.json`)
        .then(response => {
          this.locations = response.data.features.map((feature, index) => {
            this.ids[feature.id] = index
            feature.id = index
            return feature
          })

          if (response.data.localisation) {
            this.translations = response.data.localisation
          }

          this.loading = false
          resolve()
        })
    })

    const getRegionsPromise = new Promise((resolve) => {
      axios.get('regions.geojson')
        .then(response => {
          this.regions = response.data
          resolve()
        })
    })

    const getMeshPromise = new Promise((resolve) => {
      axios.get('mesh.geojson')
        .then(response => {
          resolve(response.data)
        })
    })

    this.map = new mapboxgl.Map({
      container: 'map',
      style: this.cn ? 'cn-style/style.json' : 'mapbox://styles/tourismaustralia/ck54p8nwr07pv1cmiww7c7spf',
      center: [132.1330642, -25.6096439],
      zoom: 3,
      bounds: this.mapBounds ? [this.mapBounds.split(',').slice(0, 2), this.mapBounds.split(',').slice(2)] : initialMapBounds,
      fitBoundsOptions: initialMapBoundsOptions,
      maxZoom: 10,
      maxPitch: 0,
      pitchWithRotate: false,
      dragRotate: false,
      transformRequest: (url) => {
        if (url.startsWith('self://')) {
          return { url: new URL(url.substr('self://'.length), window.location.href).href }
        }
      },
      localIdeographFontFamily: ['zh-cn',' zh-hk', 'ja-jp', 'ko-kr'].includes(this.lang) ? "'Noto Sans', 'Noto Sans CJK SC', sans-serif" : null // faster map loading for CJK ideographs
    })
    this.map.addControl(new mapboxgl.NavigationControl({
      showCompass: false
    }))

    const mapLoadPromise = new Promise((resolve) => {
      this.map.on('load', () => {
        this.map.resize()
        this.map.fitBounds(this.mapBounds ? [this.mapBounds.split(',').slice(0, 2), this.mapBounds.split(',').slice(2)] : initialMapBounds, initialMapBoundsOptions)
        resolve()
      })
    })

    Promise.all([getLocationsPromise, mapLoadPromise]).then(() => {
      this.map.addSource('locations', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: this.locations
        }
      });

      this.map.addLayer({
        id: 'locations',
        type: 'symbol',
        source: 'locations',
        paint: {
          'text-color': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            '#000',
            this.$vuetify.theme.themes.light.primary
          ],
          'text-halo-color': '#fff',
          'text-halo-width': 2
        },
        layout: {
          'icon-image': [
            'match',
            ['downcase', ['to-string', ['get', 'Status Icon']]],
            'smoke haze', 'smoke-haze',
            [
              'match',
              ['downcase', ['to-string', ['get', 'Status']]],
              'impacted', 'impacted',
              'partially impacted', 'partially-impacted',
              'smoke impacted', 'smoke-impacted',
              'not impacted', 'not-impacted',
              'location'
            ]
          ],
          'icon-size': 0.5,
          'icon-allow-overlap': true,
          'text-allow-overlap': false,
          'text-optional': true,
          'symbol-sort-key': ['-', 100, ['coalesce', ['to-number', ['get', 'Rank']], 0]],
          'symbol-z-order': 'auto',
          'icon-padding': 0,
          'text-padding': 0,
          'text-field': [
            'format',
            ['get', 'Destination'],
            {
              'font-scale': 1
            },
            ['get', 'Including'],
            {
              'font-scale': 0.9
            }
          ],
          'text-font': this.cn ? ['DIN Offc Pro Italic', 'Arial Unicode MS Regular'] : ['TABanjo Regular', 'Open Sans Regular','Arial Unicode MS Regular'],
          'text-radial-offset': 0.5,
          'text-variable-anchor': ['left', 'right', 'top-left', 'bottom-left', 'top-right', 'bottom-right'],
          'text-justify': 'auto'
        }
      })
      this.setFilter(this.tab);

      this.map.on('mousemove', 'locations', (e) => {
        if (e.features.length) {
          if (this.hoveredFeature) {
            this.map.setFeatureState({
              source: 'locations',
              id: this.hoveredFeature
            }, {
              hover: false
            })
          }

          this.hoveredFeature = e.features[0].id
          this.map.setFeatureState({
            source: 'locations',
            id: this.hoveredFeature
          }, {
            hover: true
          })
          this.map.getCanvas().style.cursor = 'pointer'
        }
      })

      this.map.on('mouseleave', 'locations', () => {
        if (this.hoveredFeature) {
          this.map.setFeatureState({
            source: 'locations',
            id: this.hoveredFeature
          }, {
            hover: false
          })
        }
        this.map.getCanvas().style.cursor = ''
      })

      this.map.on('click', 'locations', (e) => {
        if (e.features.length) {
          this.tabClear = false
          this.selectedFeature = e.features[0]

          this.$gtag.event('Click', {
            'event_category': 'Bushfire Map Click',
            'event_label': `${e.features[0].properties.Destination}|${e.features[0].properties.Status}`
          })
        }
      })
    })

    Promise.all([getRegionsPromise, mapLoadPromise]).then(() => {
      this.map.addSource('regions', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: this.regions.features
            .map((feature) => {
              if (feature.properties.TR_CODE16 in this.ids) {
                feature.id = this.ids[feature.properties.TR_CODE16]
                return feature
              } else {
                return null
              }
            })
            .filter(i => !!i)
        }
      })

      this.map.addLayer({
        id: 'regions-fill',
        type: 'fill',
        source: 'regions',
        paint: {
          'fill-opacity': 0
        }
      })

      this.map.addLayer({
        id: 'regions',
        type: 'line',
        source: 'regions',
        paint: {
          'line-width': 4,
          'line-offset': 2,
          'line-blur': 3,
          'line-opacity': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            0.3,
            0
          ],
          'line-color': '#000'
        },
        layout: {
        }
      }, this.map.getLayer('locations') ? 'locations' : null)

      this.map.on('mousemove', 'regions-fill', (e) => {
        if (e.features.length > 0) {
          if (this.regionHoveredStateId) {
            this.map.setFeatureState({
              source: 'regions',
              id: this.regionHoveredStateId
            }, {
              hover: false
            })
          }
          this.regionHoveredStateId = e.features[0].id
          this.map.setFeatureState({
            source: 'regions',
            id: this.regionHoveredStateId
          }, {
            hover: true
          })
        }
      })

      this.map.on('mouseleave', 'regions-fill', () => {
        if (this.regionHoveredStateId) {
          this.map.setFeatureState({
            source: 'regions',
            id: this.regionHoveredStateId
          }, {
            hover: false
          })
        }
        this.regionHoveredStateId = null
      })
    })

    Promise.all([getMeshPromise, mapLoadPromise]).then((resolvedValues) => {
      this.map.addSource('mesh', {
        type: 'geojson',
        data: resolvedValues[0]
      })

      this.map.addLayer({
        id: 'mesh',
        type: 'line',
        source: 'mesh',
        paint: {
          'line-dasharray': [0, 3]
        },
        layout: {
          'line-cap': 'round'
        }
      }, this.map.getLayer('water_label') ? 'water_label' : null)
    })
  },
  data: () => ({
    mapboxglSupported: true,
    lang: 'en',
    panelWidth: 400,
    translations: {
    },
    disclaimerModal: false,
    mobileDisclaimerButton: true,
    loading: true,
    tab: 'locations',
    tabClear: true, // clear feature selection on change tab
    infoModal: false, // toggle for mobile info modal
    map: null,
    location: null,
    ids: {}, // mapping of original string ID to numeric ID
    locations: [],
    selectedFeature: null,
    hoverFeature: null,
    hoverFeatureId: null,
    priorMapBounds: null, // map bounds prior to selecting a feature
    regionHoveredStateId: null
  }),
  computed: {
    tabLabelClass () {
      return `tab-label lang-${this.lang}`
    },
    bottomNavTextClass () {
      return `lang-${this.lang}`
    },
    navDrawerHeight () {
      const heights = {
        'id-id': 93,
        'ko-kr': 100
      }
      return (this.lang && this.lang in heights) ? heights[this.lang] : 88
    },
    allLocations () {
      return this.locations
    },
    impactedLocations () {
      return this.locations
        .filter(i => i.properties.Status.toLowerCase() === 'impacted')
    },
    partiallyImpactedLocations () {
      return this.locations
        .filter(i => i.properties.Status.toLowerCase() === 'partially impacted')
    },
    smokeImpactedLocations () {
      return this.locations
        .filter(i => i.properties.Status.toLowerCase() === 'smoke impacted')
    },
    notImpactedLocations () {
      return this.locations
        .filter(i => i.properties.Status.toLowerCase() === 'not impacted')
    }
  },
  watch: {
    tab: function (tab) {
      if (this.tabClear) {
        this.clearFeature()
      } else {
        // reset for next time
        this.tabClear = true
      }

      this.setFilter(tab)
    },
    selectedFeature: function (selectedFeature) {
      this.infoModal = selectedFeature ? true : false
    },
    '$vuetify.breakpoint.xs': function () {
      // when the breakpoint changes the left nav drawer closes and causes an animation of the map,
      // this keeps track of when that ends to resize the map to the new div size
      window.setTimeout(() => {
        this.map.resize()
      }, 300)
    }
  },
  methods: {
    locationIcon: function (status, statusIcon) {
      if (status && status.toLowerCase() === 'partially impacted' && statusIcon && statusIcon.toLowerCase() === 'smoke haze') {
        return require('./assets/smoke-haze.svg')
      } else {
        if (status.toLowerCase() === 'impacted') {
          return require('./assets/impacted.svg')
        } else if (status.toLowerCase() === 'partially impacted') {
          return require('./assets/partially-impacted.svg')
        } else if (status.toLowerCase() === 'smoke impacted') {
          return require('./assets/smoke-impacted.svg')
        } else if (status.toLowerCase() === 'not impacted') {
          return require('./assets/not-impacted.svg')
        }
      }
    },
    setFilter: function (tab) {
      const status = {
        'impacted': 'Impacted',
        'partially-impacted': 'Partially impacted',
        'smoke-impacted': 'Smoke impacted',
        'not-impacted': 'Not impacted'
      }

      if (this.map && this.map.getLayer('locations')) {
        if (tab === 'locations') {
          this.map.setFilter('locations', null)
        } else {
          this.map.setFilter('locations', ['==', 'Status', status[tab]])
        }
      }
    },
    tabInput: function (tab) {
      this.tab = tab
    },
    clearFeature: function () {
      this.selectFeature(null)

      if (this.priorMapBounds) {
        this.map.fitBounds(this.priorMapBounds)
        this.priorMapBounds = null
      }
    },
    selectFeature: function (feature) {
      this.selectedFeature = feature

      if (feature) {
        this.priorMapBounds = this.map.getBounds()

        this.map.flyTo({
          center: feature.geometry.coordinates,
          zoom: 5
        })
      }
    },
    listItemHover: function (item) {
      this.hoverFeature = item
      this.hoverFeatureId = item.id
    },
    listItemDeHover: function () {
      this.hoverFeatureId = null
    },
    iconKey: function (properties) {
      return Object.assign({}, properties, { icon: this.locationIcon(properties.Status, properties.StatusIcon) })
    },
    normaliseKeys: function (properties) {
      const result = Object.fromEntries(
        Object.entries(properties).map(
          ([k, v]) => [k.replace(/ /g, ''), (k === 'Links' && v && typeof v === 'string') ? JSON.parse(v) : v]
        )
      )
      Object.keys(result).forEach((key) => (key === '') && delete result[key])
      return result
    },
    /**
     * Function that registers a click on an outbound link in Analytics.
     * This function takes a valid URL string as an argument, and uses that URL string
     * as the event label. Setting the transport method to 'beacon' lets the hit be sent
     * using 'navigator.sendBeacon' in browser that support it.
     */
    getOutboundLink: function (category, url, external) {
      this.$gtag.event('Click', {
        'event_category': category,
        'event_label': url,
        'transport_type': 'beacon',
        'event_callback': function(){ if (!external) { document.location = url }}
      })
    }
  }
};
</script>
<style>
html {
  overflow-y: auto !important;
}
#map {
  height: 100%;
}
.v-content__wrap {
  height: 100vh;
}
.v-slide-group__prev, .v-slide-group__next {
  display: none !important;
}
.v-slide-group__content {
  max-width: 350px !important;
  max-width: fit-content !important;
  min-width: 100% !important;
}
.v-tab {
  font-size: smaller !important;
  padding-left: 4px !important;
  padding-right: 4px !important;
  justify-content: flex-end !important;
  letter-spacing: 0em !important;
  min-width: 70px !important;
}
.v-tab .tab-label {
  width: 100%;
  overflow-wrap: break-word;
}
.v-tab .tab-label.lang-id-id {
  letter-spacing: -1px;
}
.v-tab .tab-label.lang-it-it {
  letter-spacing: -1px;
}
.v-tab .tab-label.lang-fr-fr {
  letter-spacing: -1px;
}

.v-bottom-navigation .v-btn .lang-it-it {
  letter-spacing: 0px;
}
.v-bottom-navigation .v-btn .lang-fr-fr {
  letter-spacing: 0px;
}
.v-bottom-navigation .v-btn .lang-id-id {
  line-height: 1.0 !important;
}
.v-bottom-navigation .v-btn .v-btn__content span {
  width: 100% !important;
}

.v-tab--active {
  background-color: lightgrey;
}
.v-list-item__avatar {
  height: 35px !important;
}
.v-expansion-panel-header__icon>.v-icon {
  color: white !important;
}
.v-snack {
  margin-bottom: 95px;
  margin-left: 10px;
  right: auto !important;
  left: 0 !important;
  z-index: 100 !important;
}
.v-snack__wrapper {
  min-width: 85px !important;
  max-height: 35px !important;
}
.v-snack__content {
  padding: 0 !important;
}
.v-bottom-navigation {
  height: 66px !important;
}
.v-item-group.v-bottom-navigation .v-btn {
  white-space: pre-wrap;
  min-width: 65px !important;
  justify-content: flex-end !important;
  margin-top: 5px;
}
.v-item-group.v-bottom-navigation .v-btn .v-btn__content {
  width: 100% !important;
}
</style>
